
import React from "react";
import {
  AdminRole,
  Company,
  Config,
  Group,
  Location,
  Parents,
  Preference,
  TemplateLoaderProps,
  UserRole,
  Viewer,
} from "./types";

import {
  
  deleteCookie,
  parseId,
  serialize,
  setCookie,
  useDidMountEffect,
  useToken,
} from "../utils";
import useConfig from "./useConfig";

import { memoize, pick, sortBy } from "lodash-es";
import { defaultTimezone } from "../utils/localization/timezones";
import { defaultDateFormat } from "../utils/localization/dateFormats";
import { defaultTimeFormat } from "../utils/localization/timeFormats";
import { defaultCurrency, defaultCurrencySymbol } from "../utils/localization/currencies";
import { defaultLanguage } from "../utils/localization/languages";
import { getConfig } from "./configure";
import { useStore } from "../utils/store";
import { Roles, can } from "./roles";

import ConfigProvider from "./ConfigProvider";

import { jwtDecode } from "jwt-decode";
import moment from "moment";
import axios from "axios";

import { Device } from "@capacitor/device";
import { currencyToSymbol } from "../utils/localization/currencySymbols";
//@ts-ignore
import Ipstack from "apilayer-ipstack";
import { baseURL, ipStackKey, tokenKey } from "../global";
import { SnackbarProvider, SnackbarProviderProps } from "notistack";
import { Button } from "@mui/material";
import { MailProvider } from "./MailLoader";
import { TimerProvider } from "../modules/Timer/TimerLoader";

const defaultUiInfo = serialize({ theme: { darkMode: false, color: "#1976d2" } });
// export class CustomMomentUtils extends AdapterMoment {
//     getWeekdays() {
//         const days = this.moment.weekdaysShort(false)
//         return [...tail(days), days[0]]
//     }

//     // getWeekArray(date: Moment) {
//     //     const start = date.clone().startOf('month').startOf('isoWeek')
//     //     const end = date.clone().endOf('month').endOf('isoWeek')

//     //     let count = 0
//     //     let current = start
//     //     const nestedWeeks: Moment[][] = []

//     //     while (current.isBefore(end)) {
//     //         const weekNumber = Math.floor(count / 7)
//     //         nestedWeeks[weekNumber] = nestedWeeks[weekNumber] || []
//     //         nestedWeeks[weekNumber].push(current)

//     //         current = current.clone().add(1, 'day')
//     //         count += 1
//     //     }

//     //     return nestedWeeks
//     // }

//     // startOfWeek(date: Moment): Moment {
//     //     return date.clone().startOf('isoWeek')
//     // }

//     // endOfWeek(date: Moment): Moment {
//     //     return date.clone().endOf('isoWeek')
//     // }
// }

const TemplateChooser: React.FC<TemplateLoaderProps> = ({
  children,
  template: Temp,
  templates,
}) => {
  const config = useConfig();
  if (Temp) {
    return (
      //@ts-ignore
      <Temp {...config}>{children}</Temp>
    );
  }
  if (templates?.length) {
    const template = templates.find((t) => t.canActivate());
    if (template) {
      return (
        // @ts-ignore
        <template.component {...config}>{children}</template.component>
      );
    }
  }
  return <>{children}</>;
};

const TemplateLoaderWrapper: React.FC<TemplateLoaderProps> = (props) => {
  const notistackRef = React.createRef<any>();
  const onClickDismiss = (key?: string | number) => () => notistackRef.current?.closeSnackbar(key);

  const snackbarProviderProps: Omit<SnackbarProviderProps, "children"> = {
    disableWindowBlurListener: true,
    ref: notistackRef,
    action: (key) => (
      <Button color="inherit" onClick={onClickDismiss(key)}>
        <>Dismiss</>
      </Button>
    ),
    dense: false,
    anchorOrigin: { vertical: "bottom", horizontal: "center" },
    maxSnack: 5,
    autoHideDuration: 10000,
  };
  return (
    <SnackbarProvider {...snackbarProviderProps}>
      <TemplateChooser {...props} />
    </SnackbarProvider>
  );
};

const InnerComponent: React.FC<TemplateLoaderProps> = (props) => {
  if (props.isAmp) {
    return <TemplateLoaderWrapper {...props} />;
  }
  return <TemplateLoaderWrapper {...props} />;
};

const parseViewer = memoize((viewer?: Viewer | null, admin?: boolean): Partial<Config> => {
    const _ = sortBy(viewer?.groups, 'id')
    const comp = sortBy(viewer?.companies, 'id')
    const filtered = admin ? _.filter((g) => Boolean(g?.companies?.find(Boolean))) : _
    const filteredCompanies = admin ? comp.filter(Boolean) : comp
    const groups = !admin
        ? filtered
        : filtered.map((g) => ({
            ...g,
            companies: g.companies?.filter(Boolean),
        }))

    const companies = filteredCompanies
    const companyUserRoles = viewer?.companies?.[0]?.userRoles
    const locationUserRoles = viewer?.companies?.[0]?.locations?.[0]?.userRoles

    const userRoles =
        companyUserRoles?.role?.reduce((b, a) => {
            const n =
                a.role?.moduleRoles?.map((m) => ({
                    parent: a?.levelDetails?.id!,
                    name: m?.name!,
                    sysName: a?.role?.sysName!,
                })) || []
            return [...b, ...n]
        }, [] as UserRole[]) ||
        locationUserRoles?.role?.reduce((b, a) => {
            const n =
                a.role?.moduleRoles?.map((m) => ({
                    parent: a?.levelDetails?.id!,
                    name: m?.name!,
                    sysName: a?.role?.sysName!,
                })) || []
            return [...b, ...n]
        }, [] as UserRole[]) ||
        []

    return {
        viewer,
        groups,
        userRoles,
        companies,
    }
})
const parsePreference = memoize((preference: Preference): Config => {
  const timezone = preference?.timezone || defaultTimezone;
  const language = preference?.language || defaultLanguage;
  const dateFormat = preference?.dateFormat || defaultDateFormat;
  const timeFormat = preference?.timeFormat || defaultTimeFormat;
  const currency = preference?.currency || defaultCurrency;
  const currencySymbol = preference?.currencySymbol || defaultCurrencySymbol;
  const uiInfo = preference?.uiInfo || defaultUiInfo;
  return {
    timezone,
    language,
    dateFormat,
    timeFormat,
    currency,
    currencySymbol,
  };
});
const parseGroup = (groups?: Group[], id?: string) => {
  let group = groups?.find((g) => g?.id === id);
  if (!group) {
    group = groups?.find((g) => g?.companies?.find((c) => c?.locations?.length));
  }
  if (!group) {
    group = groups?.find((g) => g?.companies?.length);
  }
  const groupId = group?.id;
  return {
    groupId,
    group,
  };
};
const parseCompany = (companies?: Company[], id?: string, viewer?: Viewer, _roleId?: string) => {
    let company = companies?.find((c) => c?.id === id)

    if (!company) {
        company = companies?.find((c) => c?.locations?.length)
    }

    if (!company) {
        company = companies && companies[0]
    }

    const companyId = company?.id

    let userRole = company?.userRoles?.role?.find(r => r?.roleId! === _roleId)

    if (!userRole) {
        userRole = company?.userRoles?.role?.[0]
    }

    const roleId = userRole?.roleId

    const apps =
        userRole?.apps
            ?.filter((a) => a?.active ?? a?.isActive)
            .reduce((a, b) => [...a, b?.name || '', ...(b?.serviceModules || [])], [] as string[])
            .filter(Boolean) || []

    const allApps = [...apps, ...(getConfig()?.defaultApps ?? [])]
    const disabledApps = [
        ...(company?.companySettings?.customization?.disableApps || []),
        ...(company?.roleLevelCustomization?.disableApps || []),
    ]
    const activeApps = disabledApps ? allApps.filter((app) => !disabledApps.includes(app)) : allApps

    const userRoles =
        company?.userRoles?.role?.filter(r => {
            if (!roleId) return true
            return r.roleId === roleId
        })?.reduce((b, a) => {
            const n =
                a.role?.moduleRoles?.map((m) => ({
                    parent: a?.levelDetails?.id!,
                    name: m?.name!,
                    sysName: a?.role?.sysName!,
                    level: a?.role?.level,
                })) || []
            return [...b, ...n]
        }, [] as UserRole[]) || []

    const authToken = userRole?.authToken
    return {
        companyId,
        company,
        activeApps,
        userRoles,
        authToken,
        roleId,
        currency: company?.preference?.currency ?? viewer?.currency! ?? defaultCurrency,
        currencySymbol:
            company?.preference?.currencySymbol ??
            viewer?.currencySymbol ??
            currencyToSymbol(defaultCurrency),
    }
}
const parseLocation = (locations?: Location[], id?: string, viewer?: Viewer, _roleId?: string) => {
    let location = locations?.find((l) => l?.id === id)

    if (!location) {
        location = locations && locations[0]
    }

    let userRole = location?.userRoles?.role?.find(r => r?.roleId! === _roleId)

    if (!userRole) {
        userRole = location?.userRoles?.role?.[0]
    }

    const locationId = location?.id
    const roleId = userRole?.roleId

    const userRoles =
        location?.userRoles?.role?.filter(r => {
            if (!roleId) return true
            return r.roleId === roleId
        })?.reduce((b, a) => {
            const n =
                a.role?.moduleRoles?.map((m) => ({
                    parent: a?.levelDetails?.id!,
                    name: m?.name!,
                    sysName: a?.role?.sysName!,
                    level: a?.role?.level,
                })) || []
            return [...b, ...n]
        }, [] as UserRole[]) || []
    const activeApps =
        userRole?.apps
            ?.filter((a) => a?.active ?? a?.isActive)
            .reduce((a, b) => [...a, b?.name || '', ...(b?.serviceModules || [])], [] as string[])
            .filter(Boolean) || []
    const authToken = userRole?.authToken
    return {
        locationId,
        location,
        currency:
            location?.invoiceCurrency?.currency ??
            location?.preference?.currency ??
            viewer?.currency! ??
            defaultCurrency,
        currencySymbol:
            location?.invoiceCurrency?.currencySymbol ??
            location?.preference?.currencySymbol ??
            viewer?.currencySymbol ??
            currencyToSymbol(defaultCurrency),
        userRoles,
        activeApps,
        authToken,
        roleId
    }
}

const TemplateLoader: React.FC<TemplateLoaderProps> = (props) => {
  const key = ipStackKey;
  const { viewer, ...configFromViewer } = parseViewer(props.viewer, props.admin);
  const [deviceId, setDeviceId] = React.useState("");

  const preference = parsePreference(
    props.preference || {
      ...(props.viewer?.preferences || {}),
      currency: configFromViewer.currency,
      currencySymbol: configFromViewer.currencySymbol,
    }
  );

  const [parents, setParents] = useStore<Parents>("parents", {});
  const [config, _setConfig] = React?.useState<Config>({ ...preference, ...parents });
  const [networkStatus, setNetworkStatus] = React.useState(true);
  const { groupId, group } = parseGroup(
    props.groups || configFromViewer.groups,
    props.groupId || config.groupId
  );
  const {
        companyId,
        company,
        activeApps: companyActiveApps,
        userRoles: companyUserRoles,
        authToken: companyAuthToken,
        roleId:companyRoleId,
    } = parseCompany(
        props.companies || configFromViewer?.companies || group?.companies,
        props.companyId || config.companyId,
        props?.viewer!,
        config?.roleId
    )
  const {
        locationId,
        location,
        userRoles: locationUserRoles,
        currency,
        currencySymbol,
        authToken: locationAuthToken,
        activeApps: locationActiveApps,
        roleId:locationRoleId,
    } = parseLocation(
        props.locations || company?.locations!,
        props.locationId || config.locationId,
        props?.viewer!,
        config?.roleId
    )

  const authToken = React.useMemo(() => {
        return companyAuthToken ? companyAuthToken : locationAuthToken
    }, [companyAuthToken, locationAuthToken])

    const roleId = React.useMemo(()=>{
        return companyRoleId ? companyRoleId : locationRoleId
    },[companyRoleId, locationRoleId])

    const companies = sortBy(
        (configFromViewer?.companies as Company[]) || (group?.companies as Company[]),
        'id'
    )

    const locations = company?.locations as Location[]
    const activeLocations = locations?.filter((loc) => loc.active)

  React.useEffect(() => {
    setParents({ groupId, companyId, locationId, userId: props.viewer?.id });
  }, [groupId, companyId, locationId, props.viewer?.id]);

  const getDeviceId = async () => {
    const info = await Device.getId();
    setDeviceId(info.identifier);
  };
  React.useEffect(() => {
    getDeviceId();
  }, []);

  //implementation only for matter suite
  React.useEffect(() => {
    if (companies?.length) {
      const refreshToken = props?.viewer?.refreshToken!;
      setCookie(tokenKey, authToken!);
      setCookie("refreshToken", refreshToken);
    }
  }, [props.viewer?.id, companyId, companies]);

  React.useEffect(() => {
    if (companies?.length) {
      setCookie("level", serialize({ locationId }));
    }
  }, [companyId, companies]);

  //implementation only for matter suite. for refereshing token

  const worker = new Worker(
    URL.createObjectURL(
      new Blob([
        `
        let count = 0;
        setInterval(() => {
          count++;
          self.postMessage(count);
        }, 10000);
    `,
      ])
    )
  );
  const refreshingFunction = (token: string, refreshToken: string) => {
    if (!token) return;
    const user: any = jwtDecode(token!);
    let isExpired = moment.unix(user.exp).subtract(5, "minute").diff(moment()) < 1;

    if (token !== "" && refreshToken !== "") {
      if (isExpired) {
        isExpired = false;
        axios
          .post(
            `${baseURL}/Auth/RefreshToken`,
            {
              loginIp: "0.0.0.0",
              refreshToken: refreshToken,
            },
            { headers: { Authorization: `Bearer ${token}`, deviceId } }
          )
          .then((response) => {
            if (response?.data!) {
              const res = response?.data!;
              if (res.succeeded) {
                const authToken = res?.data?.authToken! ?? "";
                const refreshToken = res?.data?.refreshToken! ?? "";
                setCookie(tokenKey, authToken);
                setCookie("refreshToken", refreshToken);
              }
            }
          })
          .catch((error) => {
            if (error) {
              deleteCookie(tokenKey);
              deleteCookie("refreshToken");
              deleteCookie("level");
            }
          });
      }
    }
  };
  const handleWorker = () => {
    const { token, refreshToken } = useToken();
    refreshingFunction(token, refreshToken);
  };

  worker.addEventListener("message", handleWorker);

  React.useEffect(() => {
    return () => {
      worker.removeEventListener("message", handleWorker);
      worker.terminate();
    };
  });
  //end  refereshing token

   const employeeId = props.employeeIds?.find((empId) => parseId(empId).parent === companyId)
    const customerId = configFromViewer.userRoles?.find(
        (ur) => ur.name === Roles.CustomerLevel && parseId(ur.parent).parent === companyId
    )?.parent

    // this is for module roles
    const userRoles = React.useMemo(() => {
        return companyUserRoles.length ? companyUserRoles : locationUserRoles!
    }, [companyUserRoles, locationUserRoles])

    //this is for viewer role
    const viewerRoles = React.useMemo(() => {
        const roles = company?.userRoles?.role?.length ? company?.userRoles : location?.userRoles
        return roles
    }, [company, location])

    const isMattersuiteAdmin =
        viewerRoles?.role?.some((rol) => rol.role?.sysName === AdminRole.Owner) ?? false

    const isMattersuiteCoAdmin =
        viewerRoles?.role?.some((rol) => rol.role?.sysName === AdminRole.CoOwner) ?? false
    const isMattersuiteClient = viewerRoles?.role?.some((r) => r.role?.level === 'cli') // client
    const isMattersuiteCoCounsel = viewerRoles?.role?.some((r) => r.role?.level === 'coc') // co-counsel
    const isRoleLevelCom = viewerRoles?.role?.some(
        (r) => r.role?.level === 'com' || r.role?.level === 'cli'
    )
    const activePackages =
        company?.licenseWallet?.addons
            ?.filter(
                (addon) => addon?.defaultValue! && addon?.purchasedValue! > 0 && addon?.isActive!
            )
            .map((addon) => ({
                sysName: addon?.sysName!,
                title: addon?.title!,
                limitType: addon?.limitType!,
            })) ?? []
    const activeApps = React.useMemo(() => {
        return companyActiveApps?.length ? companyActiveApps : locationActiveApps
    }, [companyActiveApps, locationActiveApps])
  const newConfig: Config = {
    ...configFromViewer,
    viewer: { ...viewer, userRoles: viewerRoles },
    userRoles,
    isRoleLevelCom,
    roleId,
    companies,
    locations,
    activeLocations,
    ...config,
    groupId,
    group,
    companyId,
    company,
    locationId,
    location,
    activeApps,
    currency: currency,
    // currencySymbol: props.viewer?.currencySymbol! ?? currencyToSymbol(currency),
    currencySymbol: currencySymbol,
    country: props.viewer?.country!,
    employeeId,
    customerId,
    isCasefoxAdmin: props.loggedInUser?.RoleType === 1 ? true : false,
    isMattersuiteAdmin,
    isMattersuiteCoAdmin,
    isMattersuiteClient,
    isMattersuiteCoCounsel,
    logoUrl: props.logoUrl,
    baseUrl: props.baseUrl,
    employee:
      employeeId && props.employee && props.employee.id === employeeId ? props.employee : undefined,
    settings: props.settings,
    readonlyApps: [
      ...(company?.roleLevelCustomization?.readonlyApps || []),
      ...(config.readonlyApps || []),
    ],
    supportedLanguages: props.supportedLanguages,
    appBar: props.appBar,
    appBarConfig: props.appBarConfig,
    roleWrapper: props.roleWrapper,
    business: props.business,
    businessSettings: props.businessSettings,
    loggedInUser: props.loggedInUser,
    roleAndStaffType: props.roleAndStaffType,
    roleBasedObj: {
      roleType: props.loggedInUser?.RoleType!,
      roleId: props.loggedInUser?.RoleID!,
      businessId: props.loggedInUser?.BusinessID!,
      roleWrapper: props.roleWrapper!,
    },
    subscriptionInfo: props.subscriptionInfo,
    navbarPosition: props.navbarPosition ?? "Vertical",
    horizontalNavbarStyles: {},
    isSubscriptionActive: company?.licenseWallet?.isSubscriptionActive! ?? true,
    isActive: company?.licenseWallet?.isActive! ?? true,
    activePackages,
    deviceId,
  };

  const i18nProps = pick(
    config,
    "dateFormat",
    "timeFormat",
    "currency",
    "timezone",
    "language",
    "currencySymbol"
  );
  const setConfig = (conf: Partial<Config>) => {
    let _conf: Partial<Config> = { ...conf };
    // if (_conf.locationId) {
    //     _conf.companyId = parseId(_conf.locationId).parent
    // }
    // if (_conf.companyId) {
    //     _conf.groupId = parseId(_conf.companyId).parent
    // }
    _setConfig((c) => ({ ...c, ..._conf }));
  };
  React.useEffect(() => setConfig(preference), [...Object.values(preference)]);
  const refetchViewer = props.refetchViewer || (() => Promise.resolve());

  const isEmployee = React.useMemo(
    () =>
      !(
        can([], groupId, configFromViewer.userRoles ?? []) ||
        can([], companyId, configFromViewer.userRoles ?? []) ||
        can([], locationId, configFromViewer.userRoles ?? [])
      ) && Boolean(employeeId),
    [groupId, companyId, locationId, employeeId, configFromViewer.userRoles]
  );
  /// To Get Client (ISP) Information
  const getIP = () => {
    const { token } = useToken();
    if (!token) {
      const ipstack = new Ipstack(key);
      ipstack.requestIP().then((res:any) => {
        if (res) {
          const info = serialize(res);
          localStorage.setItem("clientmetadata", info);
        }
      });
    }
  };

  React.useEffect(() => {
    getIP();
  }, []);

  // reset IP when network changed
  useDidMountEffect(() => {
    if (networkStatus) {
      const ipstack = new Ipstack(key);
      ipstack.requestIP().then((res:any) => {
        if (res) {
          const info = serialize(res);
          localStorage.setItem("clientmetadata", info);
        }
      });
    }
  }, [networkStatus]);

  return (
    <ConfigProvider
      {...newConfig}
      isEmployee={isEmployee}
      refetchViewer={refetchViewer}
      setConfig={setConfig}
    >
      <MailProvider>
        <TimerProvider>
          <InnerComponent {...props} />
        </TimerProvider>
      </MailProvider>
    </ConfigProvider>
  );
};

export default TemplateLoader;
